import {
  AdMob,
  AdmobConsentStatus,
  RewardAdOptions,
  AdLoadInfo,
  RewardAdPluginEvents,
  AdMobRewardItem,
  AdMobError,
} from "@capacitor-community/admob";
import { CreditConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";

const CREDIT_CONFIG = process.env.CREDIT as unknown as CreditConfigType;

export async function initialize(): Promise<void> {
  await AdMob.initialize();

  const [trackingInfo, consentInfo] = await Promise.all([
    AdMob.trackingAuthorizationStatus(),
    AdMob.requestConsentInfo(),
  ]);

  if (trackingInfo.status === "notDetermined") {
    /**
     * If you want to explain TrackingAuthorization before showing the iOS dialog,
     * you can show the modal here.
     * ex)
     * const modal = await this.modalCtrl.create({
     *   component: RequestTrackingPage,
     * });
     * await modal.present();
     * await modal.onDidDismiss();  // Wait for close modal
     **/

    await AdMob.requestTrackingAuthorization();
  }

  const authorizationStatus = await AdMob.trackingAuthorizationStatus();
  if (
    authorizationStatus.status === "authorized" &&
    consentInfo.isConsentFormAvailable &&
    consentInfo.status === AdmobConsentStatus.REQUIRED
  ) {
    await AdMob.showConsentForm();
  }
}

export function prepareAd(userId?: string): Promise<AdLoadInfo> {
  const options: RewardAdOptions = {
    adId: CREDIT_CONFIG.rewardedAdUnitId,
  };

  if (userId) {
    options.ssv = { userId };
  }

  return AdMob.prepareRewardVideoAd(options);
}

export function showPreparedAd(): Promise<AdMobRewardItem> {
  return AdMob.showRewardVideoAd();
}

export function subscribeToLoaded(listener: (info: AdLoadInfo) => void) {
  return AdMob.addListener(RewardAdPluginEvents.Loaded, listener);
}

export function subscribeToRewarded(listener: (reward: AdMobRewardItem) => void) {
  return AdMob.addListener(RewardAdPluginEvents.Rewarded, listener);
}

export function subscribeToFailedToLoad(listener: (error: AdMobError) => void) {
  return AdMob.addListener(RewardAdPluginEvents.FailedToLoad, listener);
}

export function subscribeToDismissed(listener: () => void) {
  return AdMob.addListener(RewardAdPluginEvents.Dismissed, listener);
}

export function subscribeToFailedToShow(listener: (error: AdMobError) => void) {
  return AdMob.addListener(RewardAdPluginEvents.FailedToShow, listener);
}

export function getErrorMessage(error: AdMobError): string {
  switch (error.code) {
    case 0:
      return "This indicates that something happened internally; for instance, an invalid response was received from the ad server.";
    case 1:
      return "The ad request was invalid; for instance, the ad unit ID was incorrect.";
    case 2:
      return "The ad request was unsuccessful due to network connectivity.";
    case 3:
      return "The ad request was successful, but no ad was returned due to lack of ad inventory.";
    default:
      return "An unexpected error occurred. Please try again later.";
  }
}
